import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fab,
  Badge,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Divider
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  NetworkCheck as NetworkCheckIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as GetAppIcon,
  Publish as PublishIcon,
  FileCopy as FileCopyIcon,
  VolumeUp as VolumeUpIcon,
  MicOff as MicOffIcon,
  Mic as MicIcon,
  VideocamOff as VideocamOffIcon,
  Videocam as VideocamIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from "../../translate/i18n";
import { format } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

import { AuthContext } from '../../context/Auth/AuthContext';
import { AsteriskContext } from '../../context/Asterisk/AsteriskContext';
import EndpointForm from '../../components/AsteriskSettings/EndpointForm';
import TestConnection from '../../components/AsteriskSettings/TestConnection';
import toastError from '../../errors/toastError';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: '100%',
    margin: '0 auto'
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statsGrid: {
    marginBottom: theme.spacing(3)
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
    border: `1px solid ${theme.palette.primary.main}20`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4]
    }
  },
  statsContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  statsIcon: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statsText: {
    flexGrow: 1
  },
  statsValue: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  statsLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  contentCard: {
    marginBottom: theme.spacing(2)
  },
  tableContainer: {
    maxHeight: '60vh',
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 'bold',
        borderBottom: `2px solid ${theme.palette.divider}`
      }
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light + '20'
    }
  },
  statusChip: {
    minWidth: 80,
    '&.connected': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    '&.disconnected': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    '&.registered': {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText
    },
    '&.inactive': {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.grey[50]
    }
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper
    }
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  endpointCard: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  endpointHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2)
  },
  endpointAvatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    backgroundColor: theme.palette.primary.main
  },
  endpointInfo: {
    flexGrow: 1
  },
  endpointName: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  endpointDetails: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  endpointActions: {
    display: 'flex',
    gap: theme.spacing(0.5)
  },
  endpointStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  bulkActions: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  filterSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  filterGrid: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap'
  }
}));

const AsteriskEndpoints = () => {
  const classes = useStyles();
  
  const { user } = useContext(AuthContext);
  const { endpoints: contextEndpoints, connections, users, getEndpoints, saveEndpoint, deleteEndpoint, testEndpoint } = useContext(AsteriskContext);

  // Estado principal
  const [endpoints, setEndpoints] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    registered: 0,
    unregistered: 0,
    inactive: 0,
    sip: 0,
    pjsip: 0,
    iax2: 0
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Estados da tabela
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);

  // Estados de filtros
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTechnology, setFilterTechnology] = useState('all');
  const [filterConnection, setFilterConnection] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  // Estados de modais
  const [endpointFormOpen, setEndpointFormOpen] = useState(false);
  const [testConnectionOpen, setTestConnectionOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Carregar dados iniciais
  useEffect(() => {
    loadEndpoints();
  }, []);

  // Atualizar lista quando contexto mudar
  useEffect(() => {
    if (contextEndpoints) {
      setEndpoints(contextEndpoints);
      updateStats(contextEndpoints);
    }
  }, [contextEndpoints]);

  const loadEndpoints = async () => {
    try {
      setLoading(true);
      await getEndpoints();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await getEndpoints();
    } catch (error) {
      toastError(error);
    } finally {
      setRefreshing(false);
    }
  };

  const updateStats = (endpointsList) => {
    const newStats = {
      total: endpointsList.length,
      registered: endpointsList.filter(e => e.status === 'registered').length,
      unregistered: endpointsList.filter(e => e.status === 'unregistered').length,
      inactive: endpointsList.filter(e => e.status === 'inactive').length,
      sip: endpointsList.filter(e => e.technology === 'SIP').length,
      pjsip: endpointsList.filter(e => e.technology === 'PJSIP').length,
      iax2: endpointsList.filter(e => e.technology === 'IAX2').length
    };
    setStats(newStats);
  };

  const getFilteredEndpoints = () => {
    let filtered = [...endpoints];

    // Filtro por busca
    if (searchTerm) {
      filtered = filtered.filter(endpoint =>
        endpoint.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.callerid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        endpoint.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(endpoint => endpoint.status === filterStatus);
    }

    // Filtro por tecnologia
    if (filterTechnology !== 'all') {
      filtered = filtered.filter(endpoint => endpoint.technology === filterTechnology);
    }

    // Filtro por conexão
    if (filterConnection !== 'all') {
      filtered = filtered.filter(endpoint => endpoint.connectionId === parseInt(filterConnection));
    }

    // Filtro por usuário
    if (filterUser !== 'all') {
      filtered = filtered.filter(endpoint => endpoint.userId === parseInt(filterUser));
    }

    // Ordenação
    filtered.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleEndpointSave = async (endpointData) => {
    try {
      setLoading(true);
      await saveEndpoint(endpointData);
      setEndpointFormOpen(false);
      setSelectedEndpoint(null);
      await loadEndpoints();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndpointDelete = async () => {
    try {
      setLoading(true);
      if (selectedEndpoints.length > 0) {
        // Exclusão em lote
        for (const endpointId of selectedEndpoints) {
          await deleteEndpoint(endpointId);
        }
        setSelectedEndpoints([]);
      } else if (selectedEndpoint) {
        // Exclusão individual
        await deleteEndpoint(selectedEndpoint.id);
      }
      setDeleteDialogOpen(false);
      setSelectedEndpoint(null);
      await loadEndpoints();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndpointTest = async (endpoint) => {
    try {
      setSelectedEndpoint(endpoint);
      setTestConnectionOpen(true);
    } catch (error) {
      toastError(error);
    }
  };

  const handleMenuOpen = (event, endpoint) => {
    setMenuAnchor(event.currentTarget);
    setSelectedEndpoint(endpoint);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedEndpoint(null);
  };

  const handleSelectionChange = (endpointId) => {
    setSelectedEndpoints(prev => {
      if (prev.includes(endpointId)) {
        return prev.filter(id => id !== endpointId);
      } else {
        return [...prev, endpointId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredEndpoints = getFilteredEndpoints();
    if (selectedEndpoints.length === filteredEndpoints.length) {
      setSelectedEndpoints([]);
    } else {
      setSelectedEndpoints(filteredEndpoints.map(e => e.id));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'registered':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'unregistered':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      case 'inactive':
        return <WarningIcon style={{ color: '#ff9800' }} />;
      default:
        return <WarningIcon style={{ color: '#9e9e9e' }} />;
    }
  };

  const getTechnologyIcon = (technology) => {
    switch (technology) {
      case 'SIP':
      case 'PJSIP':
        return <PhoneIcon />;
      case 'IAX2':
        return <NetworkCheckIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const locale = i18n.language === 'pt' ? ptBR : i18n.language === 'es' ? es : enUS;
    return format(date, 'dd/MM/yyyy HH:mm', { locale });
  };

  const filteredEndpoints = getFilteredEndpoints();
  const paginatedEndpoints = filteredEndpoints.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className={classes.root}>
      {/* Cabeçalho da página */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          <GroupIcon />
          {i18n.t('asterisk.endpoints.title')}
        </Typography>
        <Box className={classes.actionButtons}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? i18n.t('common.refreshing') : i18n.t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setEndpointFormOpen(true)}
          >
            {i18n.t('asterisk.endpoints.addEndpoint')}
          </Button>
        </Box>
      </Box>

      {/* Cards de estatísticas */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#2196f3' + '20' }}>
                <GroupIcon style={{ color: '#2196f3' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.total}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.endpoints.stats.total')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#4caf50' + '20' }}>
                <CheckCircleIcon style={{ color: '#4caf50' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.registered}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.endpoints.stats.registered')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#f44336' + '20' }}>
                <ErrorIcon style={{ color: '#f44336' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.unregistered}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.endpoints.stats.unregistered')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.statsContent}>
              <Box className={classes.statsIcon} style={{ backgroundColor: '#ff9800' + '20' }}>
                <PhoneIcon style={{ color: '#ff9800' }} />
              </Box>
              <Box className={classes.statsText}>
                <Typography className={classes.statsValue}>{stats.sip + stats.pjsip}</Typography>
                <Typography className={classes.statsLabel}>
                  {i18n.t('asterisk.endpoints.stats.sipEndpoints')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Card principal */}
      <Card className={classes.contentCard}>
        {/* Seção de filtros */}
        <Box className={classes.filterSection}>
          <Grid container spacing={2} className={classes.filterGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={i18n.t('asterisk.endpoints.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.endpoints.status')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={i18n.t('asterisk.endpoints.status')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  <MenuItem value="registered">{i18n.t('asterisk.endpoints.registered')}</MenuItem>
                  <MenuItem value="unregistered">{i18n.t('asterisk.endpoints.unregistered')}</MenuItem>
                  <MenuItem value="inactive">{i18n.t('asterisk.endpoints.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.endpoints.technology')}</InputLabel>
                <Select
                  value={filterTechnology}
                  onChange={(e) => setFilterTechnology(e.target.value)}
                  label={i18n.t('asterisk.endpoints.technology')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  <MenuItem value="SIP">SIP</MenuItem>
                  <MenuItem value="PJSIP">PJSIP</MenuItem>
                  <MenuItem value="IAX2">IAX2</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.endpoints.connection')}</InputLabel>
                <Select
                  value={filterConnection}
                  onChange={(e) => setFilterConnection(e.target.value)}
                  label={i18n.t('asterisk.endpoints.connection')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  {connections.map((conn) => (
                    <MenuItem key={conn.id} value={conn.id}>
                      {conn.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('asterisk.endpoints.user')}</InputLabel>
                <Select
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  label={i18n.t('asterisk.endpoints.user')}
                >
                  <MenuItem value="all">{i18n.t('common.all')}</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Ações em lote */}
        {selectedEndpoints.length > 0 && (
          <Box className={classes.bulkActions}>
            <Typography variant="body2">
              {i18n.t('asterisk.endpoints.selectedCount', { count: selectedEndpoints.length })}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                size="small"
              >
                {i18n.t('common.delete')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Progress bar para refresh */}
        {refreshing && <LinearProgress />}

        {/* Tabela */}
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Switch
                    checked={selectedEndpoints.length === filteredEndpoints.length && filteredEndpoints.length > 0}
                    indeterminate={selectedEndpoints.length > 0 && selectedEndpoints.length < filteredEndpoints.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.name')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.technology')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.status')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.callerid')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.user')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.connection')}</TableCell>
                <TableCell>{i18n.t('asterisk.endpoints.lastActivity')}</TableCell>
                <TableCell align="center">{i18n.t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEndpoints.map((endpoint) => (
                <TableRow
                  key={endpoint.id}
                  className={`${classes.tableRow} ${selectedEndpoints.includes(endpoint.id) ? 'selected' : ''}`}
                >
                  <TableCell padding="checkbox">
                    <Switch
                      checked={selectedEndpoints.includes(endpoint.id)}
                      onChange={() => handleSelectionChange(endpoint.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar className={classes.endpointAvatar} size="small">
                        {getTechnologyIcon(endpoint.technology)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {endpoint.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {endpoint.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={endpoint.technology}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(endpoint.status)}
                      <Chip
                        label={i18n.t(`asterisk.endpoints.${endpoint.status}`)}
                        size="small"
                        className={`${classes.statusChip} ${endpoint.status}`}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{endpoint.callerid || '-'}</TableCell>
                  <TableCell>
                    {endpoint.user ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon style={{ fontSize: 18 }} />
                        {endpoint.user.name}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t('asterisk.endpoints.noUser')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {endpoint.connection ? (
                      <Chip
                        label={endpoint.connection.name}
                        size="small"
                        variant="outlined"
                        color={endpoint.connection.status === 'connected' ? 'primary' : 'default'}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t('asterisk.endpoints.noConnection')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(endpoint.lastActivity)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={i18n.t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedEndpoint(endpoint);
                            setEndpointFormOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t('asterisk.endpoints.test')}>
                        <IconButton
                          size="small"
                          onClick={() => handleEndpointTest(endpoint)}
                        >
                          <NetworkCheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t('common.more')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, endpoint)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {paginatedEndpoints.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm || filterStatus !== 'all' || filterTechnology !== 'all' || filterConnection !== 'all' || filterUser !== 'all'
                        ? i18n.t('asterisk.endpoints.noFilterResults')
                        : i18n.t('asterisk.endpoints.noEndpoints')
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={filteredEndpoints.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={i18n.t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            i18n.t('common.displayedRows', { from, to, count })
          }
        />
      </Card>

      {/* Menu de contexto */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          setEndpointFormOpen(true);
        }}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          {i18n.t('common.edit')}
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          handleEndpointTest(selectedEndpoint);
        }}>
          <ListItemIcon>
            <NetworkCheckIcon />
          </ListItemIcon>
          {i18n.t('asterisk.endpoints.test')}
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          // Implementar duplicação
        }}>
          <ListItemIcon>
            <FileCopyIcon />
          </ListItemIcon>
          {i18n.t('common.duplicate')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleMenuClose();
          setDeleteDialogOpen(true);
        }} style={{ color: '#f44336' }}>
          <ListItemIcon>
            <DeleteIcon style={{ color: '#f44336' }} />
          </ListItemIcon>
          {i18n.t('common.delete')}
        </MenuItem>
      </Menu>

      {/* FAB para adicionar endpoint */}
      <Fab
        color="primary"
        className={classes.fab}
        onClick={() => setEndpointFormOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Modais */}
      <EndpointForm
        open={endpointFormOpen}
        onClose={() => {
          setEndpointFormOpen(false);
          setSelectedEndpoint(null);
        }}
        endpoint={selectedEndpoint}
        onSave={handleEndpointSave}
        connections={connections}
        users={users}
        isEdit={!!selectedEndpoint}
        isLoading={loading}
      />

      <TestConnection
        open={testConnectionOpen}
        onClose={() => {
          setTestConnectionOpen(false);
          setSelectedEndpoint(null);
        }}
        connection={selectedEndpoint?.connection}
        onTest={testEndpoint}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {selectedEndpoints.length > 0
            ? i18n.t('asterisk.endpoints.deleteMultipleTitle')
            : i18n.t('asterisk.endpoints.deleteTitle')
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedEndpoints.length > 0
              ? i18n.t('asterisk.endpoints.deleteMultipleConfirm', { count: selectedEndpoints.length })
              : i18n.t('asterisk.endpoints.deleteConfirm', { name: selectedEndpoint?.name })
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={handleEndpointDelete}
            color="secondary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : i18n.t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overlay de loading */}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default AsteriskEndpoints;